"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLACEHOLDER_ALT, PLACEHOLDER_IMAGE } from "@/lib/constant";
import { useAuthStore } from "@/store/auth";
import { useCartStore } from "@/store/cart";
import { Trash } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { redirect } from "next/navigation";
import { useEffect } from "react";
import { useHasHydrated } from "@/hooks/use-hydration";

export default function CartPage() {
  const { token } = useAuthStore();
  const { items, removeItem, updateItem, setIsLoggedIn, setToken } =
    useCartStore();
  const authHydrated = useHasHydrated();

  useEffect(() => {
    if (!authHydrated) return;

    setToken(token);
    if (token) setIsLoggedIn(true);
    else setIsLoggedIn(false);
  }, [authHydrated, setIsLoggedIn, setToken, token]);

  if (!authHydrated) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-500">Loading your cart...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <div className="container mx-auto px-6 py-16 text-center">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-gray-700 mb-8 text-lg">
            Looks like you haven&apos;t added anything yet.
          </p>
          <Link
            href="/"
            className="bg-white text-green-800 font-bold py-3 px-8 rounded-full hover:bg-green-100 transition duration-300 shadow-lg"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const handleCheckout = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    redirect("/checkout");
  };

  const totalPrice = items.reduce(
    (total, item) => total + item.price * item.quantity,
    0,
  );
  const shipping = 60.0;
  const total = totalPrice + shipping;

  return (
    <div className="container mx-auto px-6 py-16 max-[300px]:px-1">
      <h1 className="text-4xl font-bold text-center mb-12 text-gray-800">
        Your Shopping Cart
      </h1>
      <div className="flex flex-col lg:flex-row gap-12">
        <div className="lg:w-2/3 max-[535px]:w-fit max-[535px]:mx-auto max-[300px]:w-full">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-6 space-y-4 max-[300px]:w-full max-[300px]:m-0">
            {items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center max-[535px]:flex-col gap-6 border-b border-gray-200 py-4 last:border-b-0"
              >
                <Image
                  src={item.image || PLACEHOLDER_IMAGE}
                  alt={item.name || PLACEHOLDER_ALT}
                  priority
                  width={200}
                  height={200}
                  className="object-cover w-1/4 max-[255px]:w-full rounded-lg max-[535px]:w-1/2"
                />
                <div className="flex-grow">
                  <h3 className="font-bold text-lg">{item.name}</h3>
                  <p className="text-gray-600">₹{item.price?.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-4 max-[255px]:flex-row ">
                  <p className="font-bold w-20 max-[255px]:w-full text-right">
                    ₹{item.price?.toFixed(2)}
                  </p>
                  X
                  <Input
                    type="number"
                    value={item.quantity}
                    min="1"
                    onChange={(e) => {
                      updateItem(String(item?.item_id) || "", {
                        quantity: parseInt(e.target.value),
                      });
                    }}
                    className="w-16 text-center border rounded-md p-1 bg-white/50"
                  />
                  <Button
                    onClick={() => {
                      removeItem(String(item.item_id) || "");
                    }}
                    className="text-red-500 hover:text-red-700 cursor-pointer"
                    variant={"ghost"}
                  >
                    <Trash className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:w-1/3">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sticky top-28">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">
              Order Summary
            </h2>
            <div className="flex justify-between mb-3 text-gray-700">
              <span>Subtotal</span>
              <span>₹{totalPrice?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-3 text-gray-700">
              <span>Shipping</span>
              <span>₹{shipping?.toFixed(2)}</span>
            </div>
            <hr className="my-4" />
            <div className="flex justify-between font-bold text-xl mb-6 text-gray-900">
              <span>Total</span>
              <span>₹{total}</span>
            </div>
            <Button
              onClick={handleCheckout}
              className="w-full block text-center bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition duration-300 shadow-md cursor-pointer tracking-wide"
            >
              Proceed to Checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
